import { App as AntdApp, Button, ConfigProvider, Modal } from 'antd';
import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import AuthGuard from './components/AuthGuard'; // Renamed for clarity
import MainLayout from './components/Layout/MainLayout';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CustomThemeProvider } from './contexts/CustomThemeContext';
import Login from './pages/Auth/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import SVGGeoJSONMap from './pages/Dashboard/SVGGeoJSONMap';
import LeadsList from './pages/Leads/LeadsList';
import UnassignedLeadDetail from './pages/Leads/UnassignedLeadDetail';
import UnassignedLeadsList from './pages/Leads/UnassignedLeadsList';
import PerformanceDashboard from './pages/Performance/PerformanceDashboard';
import SalesPerformanceDetail from './pages/Performance/SalesPerformanceDetail';
import LeaderboardPage from './pages/Performance/Leaderboard/LeaderboardPage';

import './App.css';
import MenuForm from './pages/Admin/MenuForm';
import MenuManagement from './pages/Admin/MenuManagement';
import RolesList from './pages/Admin/RolesList';
import SalesUsersList from './pages/Admin/SalesUsersList';
import UsersList from './pages/Admin/UsersList';
import AccessDenied from './pages/Common/AccessDenied';
import LeadDetail from './pages/Leads/LeadDetail';
import LeadForm from './pages/Leads/LeadForm';
import FollowUpActionList from './pages/MasterData/FollowUpLead/ActionList';
import FollowUpCategoryList from './pages/MasterData/FollowUpLead/CategoryList';
import FollowUpStatusList from './pages/MasterData/FollowUpLead/StatusList';
import JadwalManagement from './pages/MasterData/Jadwal';
import LeadSourceList from './pages/MasterData/Leads/LeadSourceList';
import PriorityLeadList from './pages/MasterData/Leads/PriorityLeadList';
import StatusLeadList from './pages/MasterData/Leads/StatusLeadList';
import PropertyAreaList from './pages/MasterData/Property/PropertyAreaList';
import PropertyTypesList from './pages/MasterData/Property/PropertyTypesList';
import ClosingApprovalDetail from './pages/Monitoring/ClosingApprovalDetail';
import ClosingApprovals from './pages/Monitoring/ClosingApprovals';
import TaskLeadPage from './pages/Monitoring/TaskLeadPage';
import ProfileSettingsPage from './pages/Profile/ProfileSettingsPage';
import PropertiesList from './pages/Properties/PropertiesList';
import PropertyAreaDetail from './pages/Properties/PropertyAreaDetail';
import PropertyAreaForm from './pages/Properties/PropertyAreaForm';
import PropertyAreaListPage from './pages/Properties/PropertyAreaListPage';
import PropertyDetail from './pages/Properties/PropertyDetail';
import PropertyForm from './pages/Properties/PropertyForm';
import TargetKawasanPage from './pages/RencanaLeads/TargetKawasan';
import TargetSalesPage from './pages/RencanaLeads/TargetSales';
import EvaluasiPage from './pages/Resume/Evaluasi';
import LaporanPage from './pages/Resume/LaporanPage';
import Inbox from './pages/WhatsApp/Inbox';
import Templates from './pages/WhatsApp/Templates';
import Settings from './pages/WhatsApp/Settings';
import Broadcast from './pages/WhatsApp/Broadcast';
import BroadcastManual from './pages/WhatsApp/BroadcastManual';

// Ant Design theme configuration
const theme = {
  token: {
    colorPrimary: '#7BAA18',
    colorSuccess: '#1890ff',
    colorWarning: '#faad14',
    colorError: '#ff4d4f',
    borderRadius: 6,
  },
};

const AppContent = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  useEffect(() => {
    if (user?.must_change_password) {
      setShowPasswordModal(true);
    }
  }, [user]);

  const handleRedirectToProfile = () => {
    setShowPasswordModal(false);
    navigate('/profile/settings');
  };

  return (
    <>
      <Modal
        title="Perubahan Password Diperlukan"
        open={showPasswordModal}
        closable={true}
        maskClosable={true}
        keyboard={true}
        onCancel={() => setShowPasswordModal(false)}
        footer={[
          <Button key="submit" type="primary" onClick={handleRedirectToProfile}>
            Ubah Password Sekarang
          </Button>,
        ]}
      >
        <p>Untuk keamanan akun, Anda diwajibkan untuk mengubah password Anda.</p>
      </Modal>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/access-denied" element={<AccessDenied />} />
        <Route
          path="/"
          element={
            <AuthGuard>
              <MainLayout />
            </AuthGuard>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route element={<ProtectedRoute permission="dashboard.view" />}>
            <Route path="dashboard" element={<Dashboard />} />
          </Route>

          {/* Performa Kawasan Routes */}
          <Route element={<ProtectedRoute permission="performa.view" />}>
            <Route path="performa/dashboard" element={<PerformanceDashboard />} />
            <Route path="performa/sales-detail/:id" element={<SalesPerformanceDetail />} />
            <Route path="performa/leaderboard" element={<LeaderboardPage />} />
          </Route>

          {/* Leads Routes */}
          <Route element={<ProtectedRoute permission="lead.view" />}>
            <Route path="leads/data" element={<LeadsList />} />
            <Route path="leads/unassigned" element={<UnassignedLeadsList />} />
            <Route path="leads/unassigned/:id" element={<UnassignedLeadDetail />} />
          </Route>
          <Route path="leads/create" element={<LeadForm />} />
          <Route path="leads/:id/edit" element={<LeadForm />} />
          <Route path="leads/:id" element={<LeadDetail />} />

          {/* Properties Routes */}
          <Route path="property/properties" element={<PropertiesList />} />
          <Route element={<ProtectedRoute permission="propertyArea.view" />}>
            <Route path="property/areas" element={<PropertyAreaListPage />} />
          </Route>
          <Route path="property/areas/create" element={<PropertyAreaForm />} />
          <Route path="property/areas/:id/edit" element={<PropertyAreaForm />} />
          <Route path="property/areas/:id" element={<PropertyAreaDetail />} />
          <Route path="properties/create" element={<PropertyForm />} />
          <Route path="properties/:id/edit" element={<PropertyForm />} />
          <Route path="properties/:id" element={<PropertyDetail />} />

          {/* Dashboard */}
          <Route path="dashboard-gaura" element={<SVGGeoJSONMap />} />

          {/* Admin Routes */}
          <Route element={<ProtectedRoute permission="user.view" />}>
            <Route path="admin/users" element={<UsersList />} />
          </Route>
          <Route element={<ProtectedRoute permission="salesUser.view" />}>
            <Route path="admin/sales-users" element={<SalesUsersList />} />
          </Route>
          <Route element={<ProtectedRoute permission="roles.view" />}>
            <Route path="admin/roles" element={<RolesList />} />
          </Route>
          <Route element={<ProtectedRoute permission="menu.view" />}>
            <Route path="admin/menus" element={<MenuManagement />} />
          </Route>
          <Route path="admin/menus/create" element={<MenuForm />} />
          <Route path="admin/menus/:id/edit" element={<MenuForm />} />

          {/* Resume Route */}
          <Route element={<ProtectedRoute permission="laporan.view" />}>
            <Route path="/resume/laporan" element={<LaporanPage />} />
          </Route>
          <Route element={<ProtectedRoute permission="evaluasi.view" />}>
            <Route path="resume/evaluasi" element={<EvaluasiPage />} />
          </Route>

          {/* Master Data Routes */}
          <Route path="master/property/types" element={<PropertyTypesList />} />
          <Route path="master/property/areas" element={<PropertyAreaList />} />
          <Route element={<ProtectedRoute permission="leadSource.view" />}>
            <Route path="master/lead/sources" element={<LeadSourceList />} />
          </Route>
          <Route element={<ProtectedRoute permission="leadStatus.view" />}>
            <Route path="master/lead/status" element={<StatusLeadList />} />
          </Route>
          <Route element={<ProtectedRoute permission="leadPriority.view" />}>
            <Route path="master/lead/priority" element={<PriorityLeadList />} />
          </Route>
          <Route element={<ProtectedRoute permission="leadFUCategory.view" />}>
            <Route path="master/follow-up/category" element={<FollowUpCategoryList />} />
          </Route>
          <Route element={<ProtectedRoute permission="leadFUStatus.view" />}>
            <Route path="master/follow-up/status" element={<FollowUpStatusList />} />
          </Route>
          <Route element={<ProtectedRoute permission="leadFUAction.view" />}>
            <Route path="master/follow-up/actions" element={<FollowUpActionList />} />
          </Route>
          <Route element={<ProtectedRoute permission="leadJadwalFU.view" />}>
            <Route path="master/fu-jadwal" element={<JadwalManagement />} />
          </Route>

          {/* Rencana Leads Routes */}
          <Route element={<ProtectedRoute permission="targetKawasan.view" />}>
            <Route path="rencana/target-kawasan" element={<TargetKawasanPage />} />
          </Route>
          <Route element={<ProtectedRoute permission="targetSales.view" />}>
            <Route path="rencana/target-sales" element={<TargetSalesPage />} />
          </Route>

          {/* Monitoring Routes */}
          <Route element={<ProtectedRoute permission="taskLead.view" />}>
            <Route path="monitoring/task-lead" element={<TaskLeadPage />} />
          </Route>
          <Route element={<ProtectedRoute permission="lead.closing.view" />}>
            <Route path="leads/approvals" element={<ClosingApprovals />} />
            <Route path="leads/approvals/:leadId" element={<ClosingApprovalDetail />} />
          </Route>

          {/* WhatsApp Routes */}
          <Route element={<ProtectedRoute permission="wa.inbox.read" />}>
            <Route path="whatsapp/inbox" element={<Inbox />} />
          </Route>
          <Route element={<ProtectedRoute permission="wa.template.manage" />}>
            <Route path="whatsapp/templates" element={<Templates />} />
            <Route path="whatsapp/settings" element={<Settings />} />
          </Route>
          <Route element={<ProtectedRoute permission="wa.broadcast" />}>
            <Route path="whatsapp/broadcast" element={<Broadcast />} />
            <Route path="whatsapp/broadcast-manual" element={<BroadcastManual />} />
          </Route>

          {/* Profile Routes */}
          <Route path="profile/settings" element={<ProfileSettingsPage />} />
        </Route>
      </Routes>
    </>
  );
};

function App() {
  return (
    <ConfigProvider theme={theme}>
      <AntdApp>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AuthProvider>
            <CustomThemeProvider>
              <AppContent />
            </CustomThemeProvider>
          </AuthProvider>
        </Router>
      </AntdApp>
    </ConfigProvider>
  );
}

export default App;
